import {
  Flex,
} from '@smarterservices/smarter-ui';
import VideoContainer from './VideoContainer';

import { usePlayerStore, getStore } from '../../../stores/StoreContext';
import { useState, useEffect, useRef } from 'react';
import { useFirstPaneContext } from '../../Common/SplitView/SplitViewContext';

const SidebarVideo = ({ SessionTrack, width, height }) => {
  const PlayerStore = getStore('playerStore');

  const [trackStatus, setTrackStatus] = useState(false);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const isLive = usePlayerStore((state) => state.isLive);
  const nextBoundaryRef = useRef(0);
  const lastEvalTimeRef = useRef(-1);

  // Boundary-optimized track status evaluation.
  // Only re-evaluate when currentTime crosses the next status boundary, seeks backward
  // past the last evaluation point, or the status is still unresolved (falsy).
  // This avoids N re-renders per second per sidebar track during recorded playback.
  useEffect(() => {
    const crossedForward = currentTime >= nextBoundaryRef.current;
    const seekedBackward = currentTime < lastEvalTimeRef.current - 1; // 1s tolerance for minor fluctuations
    // Use !trackStatus (not === false) so null values from unresolved live tracks
    // also trigger re-evaluation on each tick until a definitive status is obtained.
    const isInitial = !trackStatus;

    if (!crossedForward && !seekedBackward && !isInitial) return;

    const result = PlayerStore.getTrackStatusWithBoundary(SessionTrack, currentTime);
    nextBoundaryRef.current = result.nextBoundaryTime;
    lastEvalTimeRef.current = currentTime;
    setTrackStatus((prev) => prev === result.status ? prev : result.status);
  }, [currentTime, isLive]);

  // Live mode: re-evaluate when the SessionTrack receives WebRTC data.
  // In live mode, getTrackStatusWithBoundary returns nextBoundaryTime = Infinity,
  // so the boundary optimization above will never trigger a re-evaluation on its own.
  // This effect catches the transition when a track goes from no WebRTC data (null status)
  // to having WebRTC data ('connected' status) after TrackMonitor processes it.
  // The component remounts via key change in Sidebar.jsx when lastUpdated changes,
  // which causes this effect to run with the freshly populated webrtcRef.
  useEffect(() => {
    if (!isLive || !SessionTrack?.webrtcRef) return;
    const result = PlayerStore.getTrackStatusWithBoundary(SessionTrack, currentTime);
    setTrackStatus((prev) => prev === result.status ? prev : result.status);
  }, [SessionTrack?.webrtcRef, isLive]);

  if (!trackStatus) return null;

  return (
    <Flex.Item
      as="div"
      height={`${height}px`}
      margin="none none none none"
    >
      <VideoContainer
        SessionTrack={SessionTrack}
        width={width}
        height={height}
        trackStatus={trackStatus}
      />
    </Flex.Item>
  );
};

export default SidebarVideo;
